import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import * as actions from "../../../../../../../eMPower/_redux/Transload/TransportationItems/remarksActions";
import { ActionsColumnFormatter } from "./column-formatters/ActionsColumnFormatter";
import * as uiHelpers from "./RemarksUIHelper";
import { Pagination } from "../../../../../../../../../_metronic/_partials/controls";
import {
  getSelectRow,
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from "../../../../../../../../../_metronic/_helpers";
import { toAbsoluteUrl } from "../../../../../../../../../_metronic/_helpers";
import SVG from "react-inlinesvg";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useRemarksUIContext } from "./RemarksUIContext";

export function RemarksTable() {
  // Remarks UI Context
  const remarksUIContext = useRemarksUIContext();
  const remarksUIProps = useMemo(() => {
    return {
      ids: remarksUIContext.ids,
      setIds: remarksUIContext.setIds,
      queryParams: remarksUIContext.queryParams,
      setQueryParams: remarksUIContext.setQueryParams,
      productId: remarksUIContext.productId,
      openEditRemarkDialog: remarksUIContext.openEditRemarkDialog,
      openDeleteRemarkDialog: remarksUIContext.openDeleteRemarkDialog,
    };
  }, [remarksUIContext]);

  // Getting curret state of products list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.Receiving }),
    shallowEqual
  );
  const { totalCount, entities, listLoading } = currentState;
  const dispatch = useDispatch();
  useEffect(() => {
    remarksUIProps.setIds([]);
    dispatch(
      actions.fetchRemarks(remarksUIProps.queryParams, remarksUIProps.productId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remarksUIProps.queryParams, dispatch, remarksUIProps.productId]);
  const columns = [
    {
      dataField: "id",
      text: "Ln",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "PurchaseOrder",
      text: "P.O.",
      sort: true,
    },
    {
      dataField: "Sku",
      text: "SkU",
      sort: false,
    },
    {
      dataField: "Quantity",
      text: "Cartons",
      sort: false,
    },
    {
      dataField: "UnitOfMeasure",
      text: "UOM",
      sort: false,
    },
    {
      dataField: "Description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "WeightMode",
      text: "Weight Mode",
      sort: false,
    },
    {
      dataField: "Weight",
      text: "Weight",
      sort: false,
      formatter: (cell, row) => {
        return <>{row?.Weight?.toFixed(2)}</>;
      },
    },
    {
      dataField: "Cube",
      text: "Cube",
      sort: true,
      formatter: (cell, row) => {
        return <>{row?.Cube?.toFixed(2)}</>;
      },
    },
    {
      dataField: "Class",
      text: "Class",
      sort: true,
    },   
     {
      dataField: "Nmfc",
      text: "NMFC",
      sort: true,
    },
    // {
    //   dataField: "Shipped",
    //   text: "Shipped",
    //   sort: false,
    // },
    // {
    //   dataField: "OverQuantity",
    //   text: "Ovr",
    //   sort: false,
    // },
    // {
    //   dataField: "ShortQuantity",
    //   text: "Shrt",
    //   sort: false,
    // },
    // {
    //   dataField: "DamagedQuantity",
    //   text: "Dmg",
    //   sort: false,
    // },
    {
      dataField: "IsHot",
      text: "ALERTS",
      sort: true,
      formatter: (cell, row) => {
        return (
          <>
            {row?.IsHot && (
              <OverlayTrigger
                overlay={<Tooltip id="products-edit-tooltip">Hot</Tooltip>}
              >
                <a className="btn btn-icon btn-light btn-hover-light btn-sm mx-3">
                  <span className="svg-icon svg-icon-md svg-icon-danger">
                    <SVG
                      src={toAbsoluteUrl(
                        "/media/svg/icons/Communication/fire.svg"
                      )}
                    />
                  </span>
                </a>
              </OverlayTrigger>
            )}
            {row?.IsHazmat && (
              <OverlayTrigger
                overlay={
                  <Tooltip id="products-edit-tooltip">
                    Item contains Hazardous materials
                  </Tooltip>
                }
              >
                <a
                  className="btn btn-icon btn-light btn-hover-light btn-sm mx-3"
                  //onClick={() => openEditProductPage(row.id)}
                >
                  <span className="svg-icon svg-icon-md svg-icon-danger">
                    <SVG
                      src={toAbsoluteUrl(
                        "/media/svg/icons/Communication/296395.svg"
                      )}
                    />
                  </span>
                </a>
              </OverlayTrigger>
            )}
          </>
        );
      },
    },
    {
      dataField: "action",
      text: "Actions",
      formatter: ActionsColumnFormatter,
      formatExtraData: {
        openEditRemarkDialog: remarksUIProps.openEditRemarkDialog,
        openDeleteRemarkDialog: remarksUIProps.openDeleteRemarkDialog,
      },
      classes: "text-center pr-0",
      headerClasses: "text-center pr-3",
      style: {
        minWidth: "100px",
      },
    },
  ];

  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: remarksUIProps.queryParams.pageSize,
    page: remarksUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses="table-responsive"
                classes="table table-head-custom table-vertical-center"
                bordered={false}
                bootstrap4
                remote
                keyField="id"
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  remarksUIProps.setQueryParams
                )}
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
}
